// Nova AI feature colour palette.
// All hex values for the ai-project-planning modal live here — no hardcoded hex elsewhere.

// ── Nova brand ────────────────────────────────────────────────────────────────
export const NOVA_PURPLE = '#6e0fea';
export const NOVA_PURPLE_DARK = '#560eb3'; // hover / active
export const NOVA_PURPLE_DEEP = '#392664'; // gradient end (avatar, modal)
export const NOVA_PURPLE_GRADIENT_2 = '#8a2be2'; // choice-modal hover gradient end
export const NOVA_PURPLE_GRADIENT_3 = '#7e2ef5'; // choice-modal button gradient end
export const NOVA_PURPLE_GRADIENT_4 = '#9747ff'; // choice-modal card gradient end
export const NOVA_LOADING_COLOR = '#6c63ff'; // loading spinner in AiProjectPlanningModal

export const NOVA_PURPLE_MID = '#9b7edb'; // panel label, typing dot background
export const NOVA_PURPLE_MID2 = '#9c65e8'; // chip-remove button text
export const NOVA_PURPLE_LIGHT = '#c4b5e8'; // suggestion-section label
export const NOVA_PURPLE_MUTED = '#b39ddb'; // disabled send-button text
export const NOVA_PURPLE_FAINT = '#e8e4f8'; // disabled send-button background
export const NOVA_PURPLE_ICON = '#a090c8'; // attach-button icon (enabled)
export const NOVA_PURPLE_ICON_DIS = '#d0c8e8'; // attach-button icon (disabled)

// Helper: Nova purple with alpha channel — avoids duplicate rgba strings
export const novaAlpha = (a: number): string => `rgba(110,15,234,${a})`;

// Helper: success green with alpha channel
export const successAlpha = (a: number): string => `rgba(34,197,94,${a})`;

// ── Semantic text ─────────────────────────────────────────────────────────────
export const TEXT_MAIN = '#2e2541'; // primary content text
export const TEXT_AUX = '#9a96a8'; // secondary / muted text
export const TEXT_MUTED = '#a1a1a1'; // even more muted
export const TEXT_BODY = '#535353'; // body / suggestion cards
export const TEXT_MED = '#7a7896'; // medium muted (footer, card desc)
export const TEXT_FAINT = '#b0a8c8'; // very faint (choice-modal feature list)
export const TEXT_BANNER = '#3d3260'; // SOW info-banner body text
export const TEXT_PLACEHOLDER = '#c0bdd0'; // textarea placeholder
export const TEXT_GENERIC = '#666'; // generic description (choice modal)

// ── Status colours ────────────────────────────────────────────────────────────
export const ERROR_COLOR = '#ef4444'; // validation error (most uses)
export const ERROR_COLOR_2 = '#e63946'; // error variant (extraction progress)
export const ERROR_COLOR_3 = '#dc2626'; // failed-placeholder status label
export const ERROR_BG = '#fff0f0'; // stop-button background
export const ERROR_BG_HOVER = '#fee2e2'; // stop-button hover background
export const SUCCESS_COLOR = '#16a34a'; // success text / done status
export const SUCCESS_BRIGHT = '#22c55e'; // done-checklist dot / chip dot
export const WARNING_COLOR = '#f3a030'; // overallocation / warning icon
export const WARNING_BG = '#fff8e6'; // warnings banner background

// ── Surface / background ──────────────────────────────────────────────────────
export const BG_WHITE = '#fff';
export const BG_LIGHT = '#fafafa'; // subtle panels, disabled inputs
export const BG_PANEL = '#f8f8fb'; // file-card background
export const BG_DISABLED = '#f3f3f3'; // disabled pills, generic disabled bg
export const BG_BUBBLE = '#f4f4f8'; // typing-indicator bubble
export const BG_PANEL_L_START = '#fafbfd'; // left-panel gradient start
export const BG_PANEL_R_START = '#ffffff'; // right-panel gradient start
export const BG_PANEL_R_END = '#f9fafb'; // right-panel gradient end
export const BG_DOT_PENDING = '#f5f5f8'; // pending checklist dot
export const BG_DROPZONE = '#f0f6ff'; // drop-zone fill (non-dragging)
export const BG_CHOICE_CARD = '#fafafa'; // manual choice card background
export const BG_CHOICE_CARD_HIGHLIGHT_END = '#f0ebf8'; // highlighted card gradient end
export const BG_CHOICE_CARD_NONHIGHLIGHT_START = '#f3f3f7';
export const BG_CHOICE_CARD_NONHIGHLIGHT_END = '#efeff4';
export const BG_CHOICE_CARD_HOVER_START = '#f3f3f7';
export const BG_CHOICE_CARD_HOVER_END = '#efeff4';
export const BG_ICON_CIRCLE_END = '#ede8f5'; // icon circle non-highlighted gradient end

// ── Borders / dividers ────────────────────────────────────────────────────────
export const BORDER_DEFAULT = '#e8e8f0'; // chat bubble, misc cards
export const BORDER_INPUT = '#e0e0e8'; // form inputs, ExtractionCard, AttachDropdown
export const BORDER_QUICK = '#d8d8e8'; // quick-reply button
export const BORDER_DIVIDER = '#f0f0f4'; // intra-section dividers
export const BORDER_DIVIDER_LIGHT = '#f0f0f5'; // panel outer border
export const BORDER_LIGHT = '#eee'; // very light (InputArea, SuggestionCard)
export const BORDER_SPINNER = '#c8c8d8'; // spinner track
export const BORDER_DOT_PENDING = '#d0d0dc'; // pending checklist dot border
export const BORDER_DROPZONE_ICON = '#e0e8f4'; // drop-zone icon box
export const BORDER_HOVER = '#c0bdd0'; // ManualCard hover
export const BORDER_DROPZONE = '#b8cce8'; // drop-zone default border
export const BORDER_DROPZONE_PATTERN = '#adc5f5'; // drop-zone stripe pattern

// ── Figma-verified design-system tokens (TNSR-635 self-review) ────────────────
// Pulled directly from the design-system's own token values via Figma's design-context
// tool (fileKey 5iN9P3uZiqLtDd3itKnNxh), not invented — the NOVA_PURPLE-based palette
// above was never actually derived from this ticket's Figma and doesn't match it. Scoped
// to the specific elements confirmed mismatched (the SoW promo banner and the file-row
// action button); not a wholesale repaint of every custom color in this file.
export const BORDER_MIDEMPHASIS = '#0004db'; // sky/comp/button/color/border/midemphasis/default
export const TEXT_MIDEMPHASIS = '#0004db'; // sky/comp/button/color/text/midemphasis/default
export const TEXT_CONTENT_MAIN = '#232334'; // sky/sys/color/text/content/main
export const BG_NEUTRAL_2 = '#fcfbfd'; // sky/sys/color/background/neutral/2
export const BORDER_DECORATIVE = '#d3d3df'; // sky/sys/color/border/decorative
export const BORDER_ENABLED = '#6b6d7b'; // sky/sys/color/border/enabled
// Figma's upload/sparkle icon SVGs (nodes 334:2034, 240:1848) hardcode fill="black" directly —
// not bound to any design-system color variable. The reload icon (240:1847) hardcodes #232334,
// which IS TEXT_CONTENT_MAIN above, so it reuses that constant instead of duplicating the hex.
export const ICON_DEFAULT = '#000000';

// ── Misc ──────────────────────────────────────────────────────────────────────
export const ICON_BOX_BG = '#eeeef4'; // FileCardIcon background
export const DOT_AMBER = '#f59e0b'; // DropZoneIconDot (amber dot on doc icon)
export const NOVA_AVATAR_GRADIENT = `linear-gradient(135deg, ${NOVA_PURPLE} 0%, ${NOVA_PURPLE_DEEP} 100%)`;

// ── Project / avatar colour palette ───────────────────────────────────────────
// Used by COLOR_OPTIONS (avatar colour picker) and similar selectors.
export const TEXT_DISABLED_GRAY = '#b0b0b0'; // neutral-gray disabled text (not purple-tinted)
export const BORDER_SEPARATOR = '#ddd'; // light neutral separator line

// Nova-tinted surface tokens (component-specific)
export const BG_TYPING_DOT = '#9b8ec4'; // TypingDot animation dot background
export const BG_PILL_HOVER = '#ebebee'; // BudgetPill non-selected hover
export const TEXT_FIELD_LABEL = '#555'; // SowFieldLabel sentence-case text
export const BG_DELIVERABLE_ROW = '#eef4ff'; // DeliverableRow card background
export const BORDER_DELIVERABLE_ROW = '#dce8ff'; // DeliverableRow card border
export const BG_PLACEHOLDER_ROW = '#f7f5ff'; // PlaceholderRow background
export const BORDER_PLACEHOLDER_ROW = '#e4dcff'; // PlaceholderRow border
export const BG_CLIENT_DROPDOWN_HOVER = '#f3f0ff'; // ClientDropdownItem hover

export const PROJECT_COLOR_AMBER = '#E8A838';
export const PROJECT_COLOR_PURPLE = NOVA_PURPLE;
export const PROJECT_COLOR_RED = '#e63946';
export const PROJECT_COLOR_GREEN = '#5ccb66';
export const PROJECT_COLOR_BLUE = '#44b4ff';
export const PROJECT_COLOR_CORAL = '#ff7c75';
